import { useState, useEffect, useContext } from "react";
import { Formik, Form as FormikForm } from "formik";
import { Row, Col, Container } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import axios from "axios";
import "../components/button.css";
import { API_URL } from "../config/api";
import { TextField, Paper, Box } from "@mui/material";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import { UserDataContext } from "../components/UserDataContext";
import Alerts from "../components/Alerts";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../components/theme";
import FormHeader from "../components/common/FormHeader";
import PrimaryButton from "../components/common/PrimaryButton";

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Nieprawidłowy adres email")
    .required("Email jest wymagany"),
  password: Yup.string()
    .min(8, "Hasło musi mieć co najmniej 8 znaków")
    .matches(/[0-9]/, "Hasło musi zawierać co najmniej jedną cyfrę")
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      "Hasło musi zawierać co najmniej jeden znak specjalny",
    )
    .required("Hasło jest wymagane"),
  repeatpassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Hasła muszą być takie same")
    .required("Potwierdzenie hasła jest wymagane"),
});

export default function PassResetMail() {
  const [error, setError] = useState();
  const { userData } = useContext(UserDataContext);
  const [readOnly, setReadOnly] = useState(true);
  const [apiResponse, setApiResponse] = useState({});
  const [showApiResponse, setShowApiResponse] = useState(false);
  const [text, setText] = useState("");
  const navigate = useNavigate();

  const initialValues = {
    email: "",
    password: "",
    repeatpassword: "",
  };

  // Funkcja weryfikująca email wywoływana dopiero gdy email jest poprawny
  const verifyEmail = async (emailValue) => {
    if (!emailValue) return;

    try {
      const response = await axios.post(`${API_URL}/passresetmail`, {
        email: emailValue,
      });
      if (parseInt(response.data.email, 10) > 0) {
        setReadOnly(false);
        setText("Email zweryfikowany poprawnie!");
      } else {
        setReadOnly(true);
        setText(
          "Brak adresu email na liście uprawnionych lub konto jest nieaktywne",
        );
      }
    } catch (err) {
      setError(err.message);
      setReadOnly(true);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const response = await axios.post(
        `${API_URL}/registernewpassword`,
        values,
      );
      setError(response.data.error_msg);
    } catch (err) {
      console.error("Błąd podczas resetu hasła!", err);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (error === 200) {
      setApiResponse({
        title: "Informacja",
        severity: "success",
        info: "Potwierdzenie wysłane na wskazany adres email!",
      });
      setShowApiResponse(true);
      const timeoutId = setTimeout(() => {
        navigate("/login");
      }, 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [error, navigate]);

  return (
    <div>
      <FormHeader
        icon={<LockResetOutlinedIcon />}
        title="Ustaw nowe hasło"
        subtitle="Odzyskaj dostęp do swojego konta."
        description="Po zweryfikowaniu adresu e-mail będziesz mógł ustawić nowe hasło."
      />
      {showApiResponse && (
        <Row>
          <Alerts data={apiResponse} />
        </Row>
      )}
      <Container>
        <Paper
          elevation={0}
          sx={{
            border: { xs: "none", sm: "1px solid #e5e5e5" },
            p: { xs: 2, sm: 4, md: 5 },
            boxShadow: { xs: "none", sm: "0 12px 40px rgba(0,0,0,0.06)" },
          }}
        >
          <ThemeProvider theme={theme}>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({
                isSubmitting,
                handleChange,
                handleBlur,
                values,
                errors,
                touched,
              }) => (
                <FormikForm>
                  <TextField
                    autoComplete="off"
                    label="Email użytkownika"
                    name="email"
                    type="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={(e) => {
                      handleBlur(e);
                      // Odpalamy weryfikację tylko jeśli brak błędów w polu email
                      if (values.email && !errors.email) {
                        verifyEmail(values.email);
                      }
                    }}
                    fullWidth
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    margin="normal"
                  />
                  {text && (
                    <Box
                      sx={{
                        fontSize: "0.875rem",
                        color: readOnly ? "error.main" : "success.main",
                        mt: 0.5,
                        mb: 1,
                      }}
                    >
                      {text}
                    </Box>
                  )}

                  <TextField
                    label="Hasło"
                    name="password"
                    type="password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    disabled={readOnly}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    margin="normal"
                    slotProps={{
                      input: { readOnly },
                    }}
                  />

                  <TextField
                    label="Powtórz hasło"
                    name="repeatpassword"
                    type="password"
                    value={values.repeatpassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    disabled={readOnly}
                    error={
                      touched.repeatpassword && Boolean(errors.repeatpassword)
                    }
                    helperText={touched.repeatpassword && errors.repeatpassword}
                    margin="normal"
                    slotProps={{
                      input: { readOnly },
                    }}
                  />

                  {error !== "sent" && (
                    <PrimaryButton loading={isSubmitting} disabled={readOnly}>
                      Wyślij link z potwierdzeniem
                    </PrimaryButton>
                  )}
                  {error === "sent" && (
                    <h6>Wiadomość wysłana. Sprawdź pocztę.</h6>
                  )}
                </FormikForm>
              )}
            </Formik>
          </ThemeProvider>
        </Paper>
        <Box sx={{ textAlign: "center", mb: 4 }} />
      </Container>
    </div>
  );
}
